/**
 * Ambiente e URLs do painel.
 *
 * Substitui a ofuscação `String.fromCharCode` do legado ([application.js:216]) por
 * configuração legível. A base da API é resolvida por marca (config/brand.ts), mas
 * pode ser sobrescrita por VITE_API_BASE em desenvolvimento.
 */

export const DEBUG = String(import.meta.env.VITE_DEBUG ?? 'false') === 'true';

/** Base da API em produção (legado: `https://api.eminer.app/`). */
export const API_BASE_PROD = 'https://api.eminer.app/';

/** Base da API de desenvolvimento (legado: troca "api" por "admin"). */
export const API_BASE_SANDBOX = 'https://admin.eminer.app/';

/**
 * PROVISÓRIO (2026-07-13): força a API de DESENVOLVIMENTO (admin) em TODO o painel,
 * independentemente do host detectado — enquanto ligado, `api.eminer.app` (prod) não é usado.
 * Tem prioridade sobre a resolução por marca e sobre VITE_API_BASE.
 * Para voltar ao comportamento normal (prod por marca), defina como '' (vazio).
 */
export const API_BASE_FORCE_DEV = 'https://admin.eminer.app/';

/** Sobrescrita explícita da base da API (dev). Vazio = decidir por marca/sandbox. */
export const API_BASE_OVERRIDE = (import.meta.env.VITE_API_BASE ?? '').trim();

/** Marca forçada em dev (eminer | lead-machine). Vazio = detectar pelo hostname. */
export const FORCE_BRAND = (import.meta.env.VITE_FORCE_BRAND ?? '').trim();

/**
 * Chave da busca em tela cheia (proposta "Search", 17/10/2026). Ligada por padrão; com
 * VITE_SEARCH_FULLSCREEN=false a janela clássica não oferece "Experimentar a nova busca em
 * tela cheia" e quem tinha escolhido a tela cheia volta para a clássica.
 */
export const SEARCH_FULLSCREEN =
  String(import.meta.env.VITE_SEARCH_FULLSCREEN ?? 'true') !== 'false';

/**
 * Chave do onboarding em etapas e do "Complete seu perfil" (17/10/2026). Ligada por padrão desde
 * 27/10/2026, a pedido do Marcelo: quem ativa abre o onboarding no primeiro acesso (estava
 * desligada desde 17/10). VITE_ONBOARDING=false desliga; desligada, a avaliação não espera o
 * perfil ficar completo para aparecer.
 */
export const ONBOARDING_ENABLED = String(import.meta.env.VITE_ONBOARDING ?? 'true') !== 'false';

/** URLs auxiliares usadas pelo painel (portadas do objeto `urls` do legado [application.js:16]). */
export const externalUrls = {
  helpEminer: 'https://eminer.app/help',
  helpLeadMachine:
    'https://maquinadeleads.notion.site/Central-de-Ajuda-3625aeb02a3f4fe7abc96ffe92821b38',
  prospectaCom: 'https://prospectawhats.com',
  prospectaApp: 'https://prospectawhats.com',
  blog: 'https://maquinadeleads.com/blog',
  youtubeTraining:
    'https://www.youtube.com/watch?v=iSitx0kbvaI&list=PL823N6jv7FLrBhsgo_sW9lz8PP8axsawp',
  stripeEminer: 'https://billing.stripe.com/p/login/aEU02UdPscC39s4bII',
  stripeLeadMachine: 'https://billing.stripe.com/p/login/9AQ9EH77V8ZW6Va6oo',
  buyEminer: 'https://checkout.eminer.app',
  buyLeadMachine: 'https://checkout.maquinadeleads.com',
  googleMapsCid: 'https://maps.google.com/?cid=',
  whatsappSend: 'https://api.whatsapp.com/send/?phone=',
} as const;

/**
 * Páginas legais e suporte das telas sem login (proposta "Elements", 17/10/2026).
 * Termos e privacidade ficam na mesma página em cada marca (confirmado pelo Marcelo em 17/10).
 * TODO(Marcelo): canal de suporte que funciona sem login (por enquanto, a Central de Ajuda).
 */
export const legalUrls = {
  'lead-machine': {
    terms: 'https://maquinadeleads.com/termos_privacidade',
    privacy: 'https://maquinadeleads.com/termos_privacidade',
    support: externalUrls.helpLeadMachine,
    site: 'https://maquinadeleads.com',
  },
  eminer: {
    terms: 'https://eminer.app/terms/',
    privacy: 'https://eminer.app/terms/',
    support: externalUrls.helpEminer,
    site: 'https://eminer.app',
  },
} as const;

/** Serviços de geolocalização por IP (getGepIp [application.js:14791]) — 3 fallbacks. */
export const geoipUrls = {
  primary: 'https://get.geojs.io/v1/ip/geo.json',
  backup1: 'https://ipleak.net/json/',
  backup2: 'https://ipwhois.app/json/',
} as const;

/** Assets estáticos servidos pelo host legado (mesmos arquivos usados pelo painel original). */
const ASSETS_BASE = 'https://dashboard.eminer.app/images/';

/** Manutenção programada (porta de `offlineWarning` [application.js:2275]) — no legado a
 * chamada fica comentada no startup e é habilitada manualmente na janela de manutenção. */
export const MAINTENANCE_MODE = false;
export const assetUrls = {
  /** Preloader animado (dialogs de espera). */
  preloader: `${ASSETS_BASE}preloader.svg`,
  logoLeadMachine: ASSETS_BASE + 'logo_lead-machine.svg',
  logoPin: ASSETS_BASE + 'logo-pin.svg',
  logoMagnifier: ASSETS_BASE + 'logo-leadexplorer.svg',
  logoProspecta: ASSETS_BASE + 'logo-prospecta.svg',
  logoTalk: ASSETS_BASE + 'logo-talk.svg',
  logoMusic: ASSETS_BASE + 'logo-music.svg',
  /** Ícones de redes sociais (legado: urls.image_social). */
  socialBase: ASSETS_BASE + 'social/',
  /** Imagem de fallback dos cards (legado: urls.image_error). */
  imageError: ASSETS_BASE + 'error.svg',
  sparkle: ASSETS_BASE + 'sparkle.svg',
  pin: ASSETS_BASE + 'pin.png',
  pinShadow: ASSETS_BASE + 'pin-shadow.png',
} as const;
